import * as i0 from '@angular/core';
import { Injector, Injectable, inject, Pipe, NgModule, Inject } from '@angular/core';
import { AXEventService } from '@acorex/core/events';
import { Subject, filter, startWith, switchMap, of } from 'rxjs';
import { defaults, padStart, isInteger, get } from 'lodash-es';

class AXFormatter {
}

class AXFormatterRegistryService {
    constructor(injector) {
        this.plugins = [];
        this.injector = injector;
    }
    register(...plugins) {
        plugins.forEach((t) => {
            const childInjector = Injector.create({
                providers: [{ provide: t, useClass: t, deps: [] }],
                parent: this.injector,
            });
            const v = childInjector.get(t);
            if (v && !this.plugins.some((p) => p.name == v.name)) {
                this.plugins.push(v);
            }
        });
    }
    get formatters() {
        return this.plugins;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXFormatterRegistryService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXFormatterRegistryService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXFormatterRegistryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i0.Injector }] });
class AXFormatService {
    constructor(pluginRegistry) {
        this.pluginRegistry = pluginRegistry;
        this.renderSubject = new Subject();
        // Observable that other services/components can subscribe to
        this.onRender = this.renderSubject.asObservable();
    }
    render() {
        this.renderSubject.next();
    }
    /**
     * Resolves a formatter by its name.
     * @param name The name of the formatter.
     * @returns The formatter if found, otherwise throws an error.
     */
    resolveFormatter(name) {
        const formatter = this.pluginRegistry.formatters.find((c) => c.name == name);
        if (!formatter) {
            throw new Error(`Formatter not found: ${name}`);
        }
        return formatter;
    }
    // Implementation of the format method
    format(value, formatter, options) {
        if (typeof formatter === 'string') {
            const formatterRef = this.resolveFormatter(formatter);
            return formatterRef.format(value, options);
        }
        else {
            return new AXFormatFluent(this, value);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXFormatService, deps: [{ token: AXFormatterRegistryService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXFormatService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXFormatService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: AXFormatterRegistryService }] });
class AXFormatFluent {
    constructor(formatService, initialValue) {
        this.formatService = formatService;
        this.initialValue = initialValue;
    }
    /**
     * Formats the value using the specified formatter and options.
     * @param formatter The name of the formatter to use.
     * @param options The options to pass to the formatter.
     * @returns The formatted string.
     */
    to(formatter, options) {
        try {
            const formatterRef = this.formatService.resolveFormatter(formatter);
            return formatterRef.format(this.initialValue, options);
        }
        catch (error) {
            return String(this.initialValue);
        }
    }
}

class AXFormatPipe {
    constructor() {
        this.formatService = inject(AXFormatService);
        this.eventService = inject(AXEventService);
        this.triggers = ['AX_CALENDAR_CHANGED', 'AX_LANGUAGE_CHANGED', 'AX_LANGUAGE_LOADED'];
    }
    transform(value, name, options) {
        // Initial formatted value
        const initialFormattedValue = this.formatService.format(value, name, options);
        // React to language changes and re-format the value
        return this.eventService.onEvent.pipe(filter((event) => this.triggers.includes(event.type)), startWith(initialFormattedValue), // Emit the initial formatted value immediately
        switchMap((event) => {
            // Re-call format method on every language change
            const formattedValue = this.formatService.format(value, name, options);
            return of(formattedValue);
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXFormatPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXFormatPipe, name: "format" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXFormatPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'format',
                    pure: true,
                }]
        }] });

class AXNumberFormatter {
    constructor() {
        this.separatorsCache = {};
    }
    get name() {
        return 'number';
    }
    format(value, options) {
        const { thousandSeparator, decimalSeparator } = this.getLocaleSeparators(options?.locale);
        // Merge user options with default options
        const effectiveOptions = defaults({}, options, {
            thousandSeparator,
            decimalSeparator,
            zeroPadLength: 0,
            locale: navigator.language,
        });
        const sign = value < 0 ? '-' : '';
        const absoluteValue = Math.abs(value);
        // Handling zero padding for integer part
        let integerPart = Math.floor(absoluteValue).toString();
        if (effectiveOptions.zeroPadLength && effectiveOptions.zeroPadLength > integerPart.length) {
            integerPart = padStart(integerPart, effectiveOptions.zeroPadLength, '0');
        }
        // Applying thousand separator to integer part
        if (effectiveOptions.thousandSeparator) {
            integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, effectiveOptions.thousandSeparator);
        }
        // Constructing the final formatted value
        let formattedValue = sign + integerPart;
        if (effectiveOptions.decimalPlaces !== undefined) {
            // Limiting decimal places if specified
            const decimalPart = absoluteValue.toFixed(effectiveOptions.decimalPlaces).split('.')[1];
            formattedValue += effectiveOptions.decimalSeparator + decimalPart;
        }
        else if (!isInteger(value)) {
            // Showing full decimal part if decimalPlaces is not specified
            const decimalPart = absoluteValue.toString().split('.')[1] || '';
            if (decimalPart.length > 0) {
                formattedValue += effectiveOptions.decimalSeparator + decimalPart;
            }
        }
        return formattedValue;
    }
    getLocaleSeparators(locale = navigator.language) {
        // Use cache if available
        if (this.separatorsCache[locale]) {
            return this.separatorsCache[locale];
        }
        const numberFormat = new Intl.NumberFormat(locale);
        const parts = numberFormat.formatToParts(1234567.89);
        let thousandSeparator = '';
        let decimalSeparator = '';
        for (const part of parts) {
            if (part.type === 'group') {
                thousandSeparator = part.value;
            }
            else if (part.type === 'decimal') {
                decimalSeparator = part.value;
            }
        }
        // Cache the result
        this.separatorsCache[locale] = { thousandSeparator, decimalSeparator };
        return this.separatorsCache[locale];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXNumberFormatter, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXNumberFormatter }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXNumberFormatter, decorators: [{
            type: Injectable
        }] });

class AXStringFormatter {
    get name() {
        return 'string';
    }
    format(value, options) {
        if (typeof value != 'string')
            return String(value);
        const result = get(options, value)
            ? get(options, value)
            : value.replace(/{{\s*([^}]+)\s*}}/g, (match, path) => {
                return get(options, path.trim()) ?? match;
            });
        return result;
    }
}

const BUILT_IN_RULES = [AXNumberFormatter, AXStringFormatter];
class AXFormatModule {
    static forRoot(configs) {
        return {
            ngModule: AXFormatModule,
            providers: [
                ...(configs?.formatters || []),
                {
                    provide: 'AXFormatModuleFactory',
                    useFactory: (pluginRegistry) => () => {
                        pluginRegistry.register(...[...BUILT_IN_RULES, ...(configs?.formatters || [])]);
                    },
                    deps: [AXFormatterRegistryService],
                    multi: true,
                },
            ],
        };
    }
    static forChild(configs) {
        return {
            ngModule: AXFormatModule,
            providers: [
                ...(configs?.formatters || []),
                {
                    provide: 'AXFormatModuleFactory',
                    useFactory: (pluginRegistry) => () => {
                        pluginRegistry.register(...[...BUILT_IN_RULES, ...(configs?.formatters || [])]);
                    },
                    deps: [AXFormatterRegistryService],
                    multi: true,
                },
            ],
        };
    }
    /**
     * @ignore
     */
    constructor(instances) {
        instances.forEach((f) => {
            f();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXFormatModule, deps: [{ token: 'AXFormatModuleFactory' }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXFormatModule, declarations: [AXFormatPipe], exports: [AXFormatPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXFormatModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXFormatModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    exports: [AXFormatPipe],
                    declarations: [AXFormatPipe],
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['AXFormatModuleFactory']
                }] }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXFormatModule, AXFormatPipe, AXFormatService, AXFormatter, AXFormatterRegistryService, AXNumberFormatter, AXStringFormatter };
